import React, { createContext, useState, useContext, useEffect } from "react";
import authService from "../services/authService";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  /** Only blocks first paint until session is restored from storage — never toggled during login/register */
  const [isInitializing, setIsInitializing] = useState(true);

  useEffect(() => {
    // Check if user is already logged in
    const initAuth = async () => {
      try {
        console.log("Initializing auth context...");
        const currentUser = authService.getCurrentUser();
        const isAuth = await authService.isAuthenticated();

        console.log("Auth initialization:", { currentUser, isAuth });

        if (currentUser && isAuth) {
          console.log("User is authenticated, setting state");
          setUser(currentUser);
          setIsAuthenticated(true);
        } else {
          console.log("User is not authenticated");
        }
      } catch (error) {
        console.error("Error initializing auth:", error);
      } finally {
        setIsInitializing(false);
      }
    };

    initAuth();
  }, []);

  const register = async (email, name, password, passwordConfirm, wantsNewsletter = false, termsAccepted = true) => {
    const response = await authService.register(
      email,
      name,
      password,
      passwordConfirm,
      wantsNewsletter,
      termsAccepted
    );
    return response;
  };

  const verifyEmail = async (uid, token) => {
    const response = await authService.verifyEmail(uid, token);
    return response;
  };

  const login = async (userData, token) => {
    try {
      console.log("Login in AuthContext with:", { userData, token });
      // Do not set isInitializing here — unmounting the tree during login breaks OrganizationContext/reloadOrganizations

      await authService.saveSession(userData, token);

      setUser(userData);
      setIsAuthenticated(true);

      return { success: true };
    } catch (error) {
      console.error("Error in AuthContext login:", error);
      throw error;
    }
  };

  const logout = () => {
    authService.logout();
    setUser(null);
    setIsAuthenticated(false);
    // Note: OrganizationContext will be cleared by authService.logout() removing localStorage
  };

  const value = {
    user,
    isAuthenticated,
    /** True only until first session check; use for ProtectedRoute / splash */
    isLoading: isInitializing,
    isInitializing,
    register,
    verifyEmail,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthContext;
